/**
 * AMQ Topperfinder — Quiz-Definition und Ergebniskarten.
 *
 * Portierung von wp-topperfinder. Der Ablauf steckt in `amq-finder-engine.js`, die sich
 * dieser Finder mit dem Matratzenfinder teilt; hier stehen nur Fragen, Sonderregeln,
 * r-Code und Ergebnis.
 *
 * Optisch folgt der Topperfinder bewusst dem Matratzenfinder — das alte Design des Plugins
 * war jahrelang unangetastet geblieben. Deshalb dieselbe Engine, dasselbe Stylesheet und
 * dieselben Klassennamen; unterschiedlich sind nur die Fragen und das Ergebnis.
 *
 * Drei Dinge macht dieser Finder anders als der Matratzenfinder:
 *  - Neben der Empfehlung gibt es eine **Alternative**: je nach Modell ein anderer Topper
 *    mit eigenen Vorteilen oder die günstigere Basic-Ausführung mit einer Nachteilsliste.
 *    Sie steht als kompakte Karte unter der Empfehlung, nicht als zweites volles Ergebnis.
 *  - Eine Karte, für die es in der gewählten Größe keine Variante gibt, fällt weg; war das
 *    die Empfehlung, rückt die Alternative auf Platz eins nach. Das kann nur hier
 *    entschieden werden, weil die API bewusst keine Bestände kennt.
 *  - Der letzte Schritt ist ein Ausschlusskriterium: Ist die vorhandene Matratze
 *    durchgelegen, hat eine Kuhle oder ist zu weich, gibt es gar keine Empfehlung.
 */
(() => {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════════
     1. QUIZ-DEFINITION

     Werte-Codes wie im Plugin — sie stehen im r-Code und damit in jedem geteilten
     Link. Die Fragen und Optionstexte ebenfalls wörtlich aus dem Plugin-Template.
     ══════════════════════════════════════════════════════════════════════════ */

  const BREITEN = [80, 90, 100, 120, 140, 150, 160, 180, 200];
  const LAENGEN = [190, 200, 220];

  /** `validate_form_data` im Plugin: außerhalb weist schon das Formular ab. */
  const GEWICHT = { min: 35, max: 150 };

  /** Kurzform für die Fortschrittsleiste. */
  const KURZNAMEN = {
    'Härte der Matratze': 'Matratze',
    'Liegegefühl Präferenz': 'Liegegefühl',
    Schlafklima: 'Klima',
    'Effekt des Schaums': 'Schaum',
    Besonderheiten: 'Attribute',
    'Zustand der Matratze': 'Zustand',
  };

  const SCHRITTE = [
    { typ: 'groesse', hash: 'toppergroesse', kurz: 'Toppergröße' },
    { typ: 'gewicht', hash: 'koerper', kurz: 'Körpermerkmale', klasse: 'koerper validate',
      frage: 'Optimale Anpassung des Toppers an Ihren Körper',
      zweiteFrage: 'Ihr Körpertyp',
      feld: 'koerperform',
      optionen: [
        // Der Code H steht hier für breite Schultern und trägt im Plugin das Bild des
        // E-Typs — anders als im Matratzenfinder, wo H „eher schwer" bedeutet.
        { wert: 'H', info: 'Besonders breite Schultern', bild: 'koerpertyp-e', text: 'Besonders breite Schultern' },
        { wert: 'A', info: 'Besonders breites Becken', bild: 'koerpertyp-a', text: 'Besonders breites Becken' },
        { wert: 'I', info: 'Schultern & Becken ähnlich breit', bild: 'koerpertyp-i', text: 'Schultern & Becken ähnlich breit' },
        { wert: 'N', info: 'Nichts davon', bild: 'person-nichts', text: 'Nichts davon trifft zu' },
      ] },
    { hash: 'alte_matratze', kurz: 'Härte der Matratze', klasse: 'alte_matratze',
      frage: 'Wie hart ist die Matratze, auf die der Topper kommt?',
      feld: 'alte_matratze', typ: 'radio', nurText: true,
      optionen: [
        { wert: 'W', info: 'weich', text: 'WEICH' },
        { wert: 'M', info: 'mittel', text: 'MITTEL' },
        { wert: 'F', info: 'fest', text: 'FEST' },
      ] },
    { hash: 'liegegefuehl', kurz: 'Liegegefühl Präferenz', klasse: 'liegegefuehl',
      frage: 'Bevorzugtes Liegegefühl:',
      feld: 'liegegefuehl', typ: 'radio', nurText: true,
      optionen: [
        { wert: 'W', info: 'weich', text: 'WEICH' },
        { wert: 'N', info: 'neutral', text: 'NEUTRAL' },
        { wert: 'F', info: 'fest', text: 'FEST' },
      ] },
    { hash: 'schlafklima', kurz: 'Schlafklima', klasse: 'schlafklima',
      frage: 'Bevorzugtes Schlafklima:',
      feld: 'schlafklima', typ: 'radio',
      optionen: [
        { wert: 'K', info: 'kühl', bild: 'schlafklima-kalt', text: 'KÜHL', zusatz: 'Für Personen die nachts schnell schwitzen' },
        { wert: 'W', info: 'warm', bild: 'schlafklima-warm', text: 'WARM', zusatz: 'Für Personen die nachts schnell frieren' },
      ] },
    { hash: 'schaumverhalten', kurz: 'Effekt des Schaums', klasse: 'schaumverhalten',
      frage: 'Bevorzugter Effekt des Schaums:',
      feld: 'schaumverhalten', typ: 'radio',
      optionen: [
        { wert: 'M', info: 'Memoryeffekt', bild: 'memory', text: 'MEMORYEFFEKT',
          zusatz: 'Ich mag es, wenn der Schaum sich genau an meinen Körper anpasst und bei Bewegung noch kurz in dieser Position bleibt.' },
        { wert: 'K', info: 'kein Memoryeffekt', bild: 'nomemory', text: 'KEIN MEMORYEFFEKT',
          zusatz: 'Ich mag es, wenn der Schaum nach Belastung schnell zurückkommt, da ich mich in der Nacht viel bewege.' },
        { wert: 'N', info: 'neutral', bild: 'person-nichts', text: 'NEUTRAL',
          zusatz: 'Ich habe keine besondere Vorstellung; beides passt.' },
      ] },
    { hash: 'besonderheiten', kurz: 'Besonderheiten', klasse: 'besonderheiten',
      frage: 'Trifft Folgendes auf Sie zu?', hinweis: '(Mehrfachauswahl möglich)',
      feld: 'besonderheiten', typ: 'checkbox',
      optionen: [
        { wert: 'D', info: 'Druckempfindlichkeit', bild: 'person-druckempfindlich', text: 'Druckempfindlichkeit', zusatz: 'z.B. an Schulter, Hüfte oder Rippen' },
        { wert: 'K', info: 'Kribbeln', bild: 'person-kribbeln', text: 'Kribbeln/Einschlafen der Gliedmaßen' },
        { wert: 'A', info: 'Allergiker/in', bild: 'person-allergiker', text: 'Allergiker/in' },
        // Wie im Plugin vorausgewählt, ebenso „keiner der genannten Punkte“ im nächsten Schritt.
        { wert: 'N', info: 'Keine', bild: 'person-nichts', text: 'Auf mich trifft nichts davon zu', vorausgewaehlt: true },
      ] },
    { hash: 'besonderheiten_matratze', kurz: 'Zustand der Matratze', klasse: 'besonderheiten_matratze',
      frage: 'Die Matratze, auf die der Topper kommt, ist ...', hinweis: '(Mehrfachauswahl möglich)',
      feld: 'besonderheiten_matratze', typ: 'checkbox', nurText: true,
      optionen: [
        { wert: 'D', info: 'durchgelegen', text: 'durchgelegen' },
        { wert: 'K', info: 'hat eine Kuhle', text: 'hat eine Kuhle' },
        { wert: 'W', info: 'viel zu weich', text: 'ist viel zu weich' },
        // Wie im Plugin vorausgewählt: Der Normalfall kommt ohne Klick zum Ergebnis.
        { wert: 'N', info: 'nichts davon', text: 'keiner der genannten Punkte', vorausgewaehlt: true },
      ] },
  ];

  /**
   * Die Sperre am Ende. Im Plugin steht sie in `onsubmit`: Ist etwas anderes als „keiner
   * der genannten Punkte" angekreuzt, erscheint dieser Hinweis und es gibt kein Ergebnis.
   * Ein Topper kann eine verschlissene Matratze weder härter machen noch korrigieren.
   */
  const SPERRE_TEXT =
    '<p>Da wir Ihnen viele Jahre guten Schlaf bringen möchten, würden wir Ihnen in diesem Fall eine neue Matratze, anstelle des neuen Toppers empfehlen. Ein Topper würde sich schnell der Form Ihrer Matratze anpassen &amp; kann diese nicht härter machen oder korrigieren.</p>';

  const ERGEBNIS_TEXTE = {
    erfolg: 'Wir haben Ihren passenden Topper gefunden.',
    bildunterschrift: 'Ihr individuelles Ergebnis auf Basis von 5.000+ persönlichen Kundenberatungen',
    gruendeKopf: 'Warum dieser Topper zu Ihnen passt',
    kunden: '250.000 Zufriedene Kunden',
    garantie:
      'Ihre Wahl soll sich langfristig richtig anfühlen. Deshalb helfen wir jederzeit weiter – mit 10 Jahren Garantie.',
    warenkorb: 'In den Warenkorb',
    steuer: '(inkl. MwSt.)',
  };

  /* ══════════════════════════════════════════════════════════════════════════
     2. DAS REZEPT
     ══════════════════════════════════════════════════════════════════════════ */

  window.AmqFinder.starten({
    name: 'Topperfinder',
    /** Präfix der Fehlerreferenz, die der Besucher zu sehen bekommt. */
    kuerzel: 'TF',
    wurzelAttribut: 'data-tf-root',
    konfigAttribut: 'data-tf-config',
    // Dieselben IDs wie der Matratzenfinder — daran hängen 369 Regeln des gemeinsamen
    // Stylesheets. Eigene IDs hätten bedeutet, das ganze Layout zu verdoppeln.
    containerId: 'matratenfinder-container',
    finderId: 'matratzen-finder',
    fortschrittId: 'mf-progress',
    speicher: 'amq_tf_data',
    neustartText: 'Topperfinder neu starten',
    bannerZiel: '.toppergroesse .mf-body',
    kurznamen: KURZNAMEN,
    // Für den Topperfinder gibt es keine Erklärvideos — die Videokarte entfällt deshalb
    // ganz, statt leer im Kopfbereich zu stehen.
    videoTexte: null,
    hinweisKopf: 'Für diese Kombination haben wir keine Standardempfehlung',
    hinweisText: 'Melden Sie sich gern bei uns — wir beraten Sie persönlich und finden den passenden Topper für Sie.',

    schritteBauen: () => SCHRITTE,

    /**
     * Beide Ausschlussgruppen des Plugins. Anders als beim Matratzenfinder hakt es
     * „keiner der Punkte" wieder an, sobald gar nichts mehr gewählt ist — sonst ließe
     * sich der letzte Schritt leer lassen und die Sperre liefe ins Leere.
     */
    istAusschlussGruppe: (name) => name === 'besonderheiten' || name === 'besonderheiten_matratze',
    ausschlussFaelltZurueck: true,

    slideHtml(schritt, h) {
      const { esc, icon, kachel } = h;

      if (schritt.typ === 'groesse') {
        const opt = (werte, gewaehlt) =>
          werte.map((w) => `<option value="${w}"${w === gewaehlt ? ' selected' : ''}>${w} cm</option>`).join('');
        return `
<div class="swiper-slide toppergroesse" data-hash="toppergroesse" data-shortname="Toppergröße" data-video="">
  <div class="mf-body">
    <h2>Welche Größe soll Ihr Topper haben?</h2>
    <div class="flex-container">
      <div class="hg-form-group">
        <label for="topper_breite">Breite</label>
        <select class="hg-form-control" name="topper_breite" id="topper_breite">${opt(BREITEN, 90)}</select>
      </div>
      <div class="hg-form-group">
        <label for="topper_laenge">Länge</label>
        <select class="hg-form-control" name="topper_laenge" id="topper_laenge">${opt(LAENGEN, 200)}</select>
      </div>
      <div class="bild_masse">
        <img width="250" src="${icon('toppermasse')}" alt="" aria-hidden="true">
      </div>
    </div>
  </div>
</div>`;
      }

      if (schritt.typ === 'gewicht') {
        // Anders als beim Matratzenfinder wird hier nur das Gewicht erhoben — die
        // Körpergröße spielt für die Topperempfehlung keine Rolle.
        return `
<div class="swiper-slide ${schritt.klasse}" data-hash="${schritt.hash}" data-shortname="${esc(schritt.kurz)}" data-video="">
  <div class="mf-body">
    <h2>${esc(schritt.frage)}</h2>
    <div class="flex-container">
      <div class="hg-form-group">
        <label for="gewicht">Ihr Gewicht [kg]</label>
        <input type="number" inputmode="numeric" class="hg-form-control" id="gewicht" name="gewicht" placeholder="Gewicht [in kg]" min="${GEWICHT.min}" max="${GEWICHT.max}" required>
      </div>
    </div>
    <h3>${esc(schritt.zweiteFrage)}</h3>
    <div class="flex-container">${schritt.optionen.map((o) => kachel(schritt, o)).join('')}</div>
  </div>
</div>`;
      }

      return null;
    },

    /** Gewichtsgrenzen wie im Plugin-JS (`slideNextTransitionStart`). */
    pruefung(schritt, slide) {
      if (schritt.typ !== 'gewicht') return null;
      const gewicht = slide.querySelector('[name="gewicht"]')?.value ?? '';
      if (gewicht === '') return '<p>Bitte geben Sie Ihr Gewicht an.</p>';
      if (Number(gewicht) < GEWICHT.min) return '<p>Topper nicht geeignet für Babies und Kleinkinder.</p>';
      if (Number(gewicht) > GEWICHT.max) {
        return `<p>Unsere Topper sind leider nur für Personen mit einem maximalen Körpergewicht von ${GEWICHT.max} kg konzipiert.</p>`;
      }
      return null;
    },

    /** Die Sperre: verschlissene Matratze → neue Matratze statt Topper, kein Ergebnis. */
    vorErgebnis(h) {
      const gewaehlt = h.gewaehlteWerte('besonderheiten_matratze');
      return gewaehlt.some((w) => w !== 'N') ? SPERRE_TEXT : null;
    },

    /**
     * r-Code, 9 Felder — Format siehe `services/topperfinder/engine.ts` im automation-tool:
     * breite|laenge|gewicht|koerperform|alteMatratze|liegegefuehl|schlafklima|
     * schaumverhalten|besonderheiten
     */
    rCode(h) {
      return btoa([
        h.wert('topper_breite'),
        h.wert('topper_laenge'),
        h.wert('gewicht'),
        h.gewaehlt('koerperform'),
        h.gewaehlt('alte_matratze'),
        h.gewaehlt('liegegefuehl'),
        h.gewaehlt('schlafklima'),
        h.gewaehlt('schaumverhalten'),
        h.werte('besonderheiten'),
      ].join('|'));
    },

    /* ── Ergebnis ──────────────────────────────────────────────────────────── */

    /**
     * Holt zu beiden Karten Produkt und Variante. Eine Karte ohne auflösbare Variante
     * fällt weg — genau das tat auch das Plugin, nur konnte es das serverseitig
     * entscheiden. Kein Randfall: die Basic-Ausführungen führen ein kleineres
     * Größenraster, `ViscoBasic` etwa fehlt in 9 von 27 Größen.
     */
    async ergebnisAufbereiten(daten, h) {
      const loesen = async (karte) => {
        if (!karte?.shopify) return null;
        try {
          const produkt = await h.produktHolen(karte.shopify.handle);
          const variante = h.varianteFinden(produkt, karte.shopify.optionen);
          if (!variante) return null;
          // Wie auf der Produktseite für genau diese Variante; fehlt sie, fehlt nur die Zeile.
          const lieferzeit = await h.lieferzeitHolen(karte.shopify.handle, variante.id);
          return { karte, produkt, variante, lieferzeit };
        } catch (err) {
          // Die Karte fällt weg, der Besucher merkt nichts — gemeldet wird es trotzdem,
          // sonst bliebe ein umbenannter Handle unbemerkt.
          h.problemMelden({ art: 'warnung', fehler: err, code: daten.code });
          return null;
        }
      };
      // Rückt die Alternative nach vorn, ist sie die Empfehlung — so stand es auch im Plugin.
      return (await Promise.all([loesen(daten.primaer), loesen(daten.alternative)])).filter(Boolean);
    },

    ergebnisHtml(daten, karten, code, h) {
      const { esc } = h;
      if (!karten.length) {
        return `
<div class="mf-body">
  <h2>In dieser Größe haben wir keinen passenden Topper</h2>
  <p>Für die gewählten Maße gibt es unsere Empfehlung leider nicht. Melden Sie sich gern bei uns — wir beraten Sie persönlich.</p>
</div>`;
      }
      const [empfehlung, alternative] = karten;
      return `
<div class="amq-tpl-mf-result tf-result" data-mf-code="${esc(code)}">
  ${karteHtml(daten, empfehlung, h)}
  ${alternative ? alternativeHtml(alternative, empfehlung, h) : ''}
</div>`;
    },

    letztesErgebnis(daten, karten, code, h) {
      const erste = karten[0];
      if (!erste) return null;
      return {
        name: erste.karte.name || erste.produkt?.title || '',
        masse: `${daten.masse.breite} x ${daten.masse.laenge} cm`,
        zusatz: 'Topper',
        bild: erste.produkt?.featured_image ?? '',
        preisCent: erste.variante?.price ?? null,
        // Für den Block auf der Startseite, wie beim Matratzenfinder.
        handle: erste.produkt?.handle ?? '',
        variante: erste.variante?.id ?? null,
        link: `${h.KONFIG.seitenUrl}?r=${encodeURIComponent(code)}`,
      };
    },
  });

  /* ══════════════════════════════════════════════════════════════════════════
     3. DIE ERGEBNISKARTEN

     Die Empfehlung ist aufgebaut wie beim Matratzenfinder, damit das gemeinsame
     Stylesheet unverändert greift. Unterschied: Statt der Begründungs-Symbole stehen
     hier Haken — und bei der Basic-Ausführung stattdessen die Nachteile gegenüber dem
     Tencel-Bezug.

     Die Alternative darunter ist bewusst klein: Bild, Name, ihre Punkte, Preis und
     Knopf. Bildunterschrift, Siegel und Maße fehlen dort — die ersten beiden sind
     Aussagen über die Empfehlung, die Maße stehen schon darüber.
     ══════════════════════════════════════════════════════════════════════════ */

  /** Was beide Karten über das Produkt wissen müssen. */
  function produktAngaben({ karte, produkt, lieferzeit }, h) {
    const handle = karte.shopify?.handle ?? '';
    const info = h.KONFIG.produkte?.[handle] ?? {};
    return {
      handle,
      info,
      // Bewusst der Modellname der API und NICHT `produkt.title`: `climaflex-summer-topper`
      // und `basic-topper` führen beide Ausführungen im selben Shopify-Produkt, dessen Titel
      // generisch ist („ClimaFlex Summer Topper"). Erst der Modellname aus `tf_modelle`
      // unterscheidet Premium von Basic — genau den zeigte auch das WordPress-Plugin.
      name: karte.name || produkt?.title || '',
      texte: karte.texte ?? {},
      url: info.url || `/products/${handle}`,
      bild: produkt?.featured_image ?? produkt?.images?.[0] ?? '',
      lieferzeit: lieferzeit ?? '',
    };
  }

  function bewertungHtml({ handle, info, url }, h) {
    if (!(info.anzahl > 0 && info.bewertung > 0)) return '';
    return `
      <div class="mf-result__reviews">
        ${h.sterneHtml(Number(info.bewertung), Number(info.bewertungMax))}
        <a href="${h.esc(url)}#reviews" rel="nofollow" data-amq-bewertungen="${h.esc(handle)}"><span class="count">${h.esc(info.anzahl)}</span> ${info.anzahl === 1 ? 'Rezension' : 'Rezensionen'}</a>
      </div>`;
  }

  function warenkorbHtml(variante, url, klasse, h) {
    return variante?.available
      ? `<button type="button" class="amq_mf_add_to_cart ${klasse}" data-variant="${variante.id}"><span class="mf-cta-text">${ERGEBNIS_TEXTE.warenkorb}</span><span class="mf-cta-spinner" aria-hidden="true"></span></button>`
      : `<a class="amq_mf_add_to_cart ${klasse}" href="${h.esc(url)}">Zum Produkt</a>`;
  }

  function karteHtml(daten, eintrag, h) {
    const { esc, geld, HAKEN_GRUEN_SVG, PUNKT_GRUEN_SVG, KREUZ_SVG } = h;
    const { variante } = eintrag;
    const angaben = produktAngaben(eintrag, h);
    const { name, texte, lieferzeit } = angaben;
    const symbole = h.KONFIG.ergebnis ?? {};

    const kennwerte = [
      ['Breite:', `${daten.masse.breite} cm`],
      ['Länge:', `${daten.masse.laenge} cm`],
    ]
      .map(([links, rechts]) => `<div class="spec-box"><div class="prev">${esc(links)}</div><div class="nxt">${esc(rechts)}</div></div>`)
      .join('');

    // Entweder die Häkchenliste oder — bei den Basic-Ausführungen — die Nachteile.
    const punkte = texte.einleitung
      ? `<p class="tf-result__nachteil-kopf">${texte.einleitung}</p>` +
        texte.nachteile
          .map(
            (n) => `
      <div class="mf-result__feature-item tf-result__nachteil">
        <div class="mf-result__feature-icon">${KREUZ_SVG}</div>
        <div class="mf-result__feature-text">${esc(n)}</div>
      </div>`,
          )
          .join('')
      : (texte.vorteile ?? [])
          .map(
            (v) => `
      <div class="mf-result__feature-item">
        <div class="mf-result__feature-icon">${HAKEN_GRUEN_SVG}</div>
        <div class="mf-result__feature-text">${esc(v)}</div>
      </div>`,
          )
          .join('');

    const siegel = (symbole.siegel ?? [])
      .map((s) => `<div class="mf-result__trust-item"><img src="${esc(s.bild)}" alt="" loading="lazy"><p>${s.text}</p></div>`)
      .join('');

    const preis = variante
      ? `<p class="preis"><span class="amount">${geld(variante.price)}</span> <span class="tax-info">${ERGEBNIS_TEXTE.steuer}</span></p>`
      : '';

    return `
  <div class="mf-result__grid tf-result__karte">
    <div class="mf-result__gallery">
      <div class="mf-result__gallery-wrapper">
        <div class="mf-result__main-image">
          ${angaben.bild ? `<img src="${esc(angaben.bild)}" alt="${esc(name)}">` : ''}
        </div>
        <div class="mf-result__gallery-overlay"><span>${ERGEBNIS_TEXTE.bildunterschrift}</span></div>
      </div>
    </div>

    <div class="mf-result__success-msg">
      <span class="mf-result__success-icon">${HAKEN_GRUEN_SVG}</span>
      <span class="mf-result__success-text">${ERGEBNIS_TEXTE.erfolg}</span>
    </div>

    <div class="mf-result__right">
      <div class="mf-result__content">
        ${bewertungHtml(angaben, h)}
        <h2 class="mf-result__product-name">${esc(name)}</h2>
        <div class="mf-result__specs">
          <div class="mf-result__specs-inner">${kennwerte}</div>
        </div>
        <div class="mf-result__cms-headline"><p>${ERGEBNIS_TEXTE.gruendeKopf}</p></div>
        <div class="mf-result__features">${punkte}</div>
        <div class="mf-result__price">${preis}</div>
        <div class="mf-result__cta">${warenkorbHtml(variante, angaben.url, '', h)}</div>
        ${
          lieferzeit
            ? `<div class="mf-result__delivery">
          <span class="mf-result__delivery-dot">${PUNKT_GRUEN_SVG}</span>
          <span class="mf-result__delivery-text"><p>${esc(lieferzeit)}</p></span>
        </div>`
            : ''
        }
        <div class="mf-result__subfooter">${siegel}</div>
      </div>
    </div>
  </div>`;
  }

  /**
   * Die Alternative unter der Empfehlung. Ist sie günstiger — was praktisch immer der Fall
   * ist —, sagt die Überschrift das und der Preis nennt den Abstand: Genau das ist der
   * Grund, sie statt der Empfehlung zu nehmen.
   */
  function alternativeHtml(eintrag, empfehlung, h) {
    const { esc, geld, HAKEN_GRUEN_SVG, KREUZ_SVG } = h;
    const { variante } = eintrag;
    const angaben = produktAngaben(eintrag, h);
    const { name, texte, lieferzeit } = angaben;

    const ersparnis = variante && empfehlung.variante ? empfehlung.variante.price - variante.price : 0;
    const titel = ersparnis > 0 ? 'Die günstigere Alternative' : 'Ebenfalls passend für Sie';

    // Wie auf der Empfehlung: entweder eigene Vorteile oder die Nachteile der Basic-Ausführung.
    const nachteile = Boolean(texte.einleitung);
    const punkte = (nachteile ? texte.nachteile : texte.vorteile) ?? [];
    const liste = punkte.length
      ? `<ul class="tf-alternative__liste${nachteile ? ' tf-alternative__liste--nachteile' : ''}">
          ${punkte.map((p) => `<li><span class="tf-alternative__symbol">${nachteile ? KREUZ_SVG : HAKEN_GRUEN_SVG}</span>${esc(p)}</li>`).join('')}
        </ul>`
      : '';

    return `
  <section class="tf-alternative" aria-labelledby="tf-alternative-titel">
    <h3 class="tf-alternative__titel" id="tf-alternative-titel">${titel}</h3>
    <div class="tf-alternative__karte">
      <div class="tf-alternative__bild">
        ${angaben.bild ? `<img src="${esc(angaben.bild)}" alt="${esc(name)}" loading="lazy">` : ''}
      </div>
      <div class="tf-alternative__kopf">
        ${bewertungHtml(angaben, h)}
        <p class="tf-alternative__name">${esc(name)}</p>
      </div>
      <div class="tf-alternative__punkte">
        ${nachteile ? `<p class="tf-alternative__einleitung">${texte.einleitung}</p>` : ''}
        ${liste}
      </div>
      <div class="tf-alternative__kauf">
        ${
          variante
            ? `<p class="tf-alternative__preis"><span class="amount">${geld(variante.price)}</span> <span class="tax-info">${ERGEBNIS_TEXTE.steuer}</span></p>`
            : ''
        }
        ${ersparnis > 0 ? `<p class="tf-alternative__ersparnis">${geld(ersparnis)} günstiger</p>` : ''}
        ${warenkorbHtml(variante, angaben.url, 'tf-alternative__cta', h)}
        ${lieferzeit ? `<p class="tf-alternative__lieferzeit">${esc(lieferzeit)}</p>` : ''}
      </div>
    </div>
  </section>`;
  }
})();
